<template>
  <artmisads-modal
    v-model:open="visible"
    :title="st('settingsWhitelist.addModalTitle')"
    :width="1200"
    :maskClosable="false"
    @cancel="handleCancel"
    :footer="null"
  >
    <div class="publisher-modal-content">
      <div class="filter-bar">
        <div class="filter-left">
          <div class="filter-item" ref="methodRef">
            <div class="filter-label">{{ st('settingsWhitelist.colPromotionMethod') }}</div>
            <artmisads-select
              v-model:value="filter.promotionMethodId"
              style="width: 232px"
              :placeholder="st('common.promotionMethod')"
              :options="methods"
              :show-search="false"
              :allowClear="true"
              :getPopupContainer="() => methodRef"
            />
          </div>
          <div class="filter-item" ref="categoryRef">
            <div class="filter-label">{{ st('settingsWhitelist.colPromotionCategories') }}</div>
            <artmisads-select
              v-model:value="filter.promotionProductCategoryId"
              style="width: 232px"
              :placeholder="st('common.promotionCategories')"
              :options="categories"
              :show-search="false"
              :allowClear="true"
              :getPopupContainer="() => categoryRef"
            />
          </div>
        </div>
        <a-input-search
          v-model:value="keyword"
          class="search-input"
          :placeholder="st('settingsWhitelist.addModalSearchPlaceholder')"
          allow-clear
          @search="onSearch"
          @pressEnter="onSearch"
        />
      </div>

      <artmisads-table
        :columns="columns"
        :data-source="data"
        :loading="loading"
        :row-key="record => record.publisherId"
        :scroll="{ y: 400, x: 900 }"
        :row-selection="{
          selectedRowKeys,
          onChange: onSelectChange,
        }"
        :change="onPageChange"
        :pagination="{
          pageSize: params.pageSize,
          current: params.page,
          total: params.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'publisher'">
            <a-flex gap="12" align="center">
              <LogoImage
                :logoUrl="record?.avatarUrl"
                :brandName="record?.publisherName"
                :width="56"
                :height="56"
                :fontSize="12"
              />
              <span>{{ record?.publisherName }}</span>
            </a-flex>
          </template>
          <template v-if="column.dataIndex === 'promotionMethods'">
            <AutoTooltip
              :content="record?.promotionMethods ? formatNames(record.promotionMethods) : '--'"
              :width="160"
            />
          </template>
          <template v-if="column.dataIndex === 'promotionProductCategories'">
            <AutoTooltip
              :content="
                record?.promotionCategories
                  ? formatNames(record.promotionCategories)
                  : '--'
              "
              :width="200"
            />
          </template>
          <template v-if="column.dataIndex === 'bio'">
            <AutoTooltip
              :content="record?.bio ? record.bio : '--'"
              :contentWidth="500"
              :width="200"
            />
          </template>
        </template>
      </artmisads-table>
    </div>
    <div class="footer-btn">
      <span class="selected-count">
        {{ st('settingsWhitelist.selectedPublishers', { n: selectedCount }) }}
      </span>
      <artmisads-button
        type="primary"
        :disabled="!selectedCount"
        :loading="submitting"
        @click="handleSubmit"
      >
        {{ st('settingsWhitelist.addModalSubmit') }}
      </artmisads-button>
    </div>
  </artmisads-modal>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import { whitelistApi } from '@/server/seller/index'
import profileApi from '~/server/register'
import LogoImage from '@/components/LogoImage.vue'
import AutoTooltip from '@/components/AutoTooltip.vue'
import { useSellerI18n } from '@/composables/useSellerI18n'

const { st } = useSellerI18n()
const emit = defineEmits(['refresh'])

const methodRef = ref(null)
const categoryRef = ref(null)
const visible = ref(false)
const loading = ref(false)
const submitting = ref(false)
const data = ref([])
const keyword = ref('')
const selectedRowKeys = ref([])
const allSelectedRowKeys = ref(new Set())
const methods = ref([])
const categories = ref([])
const filter = ref({
  promotionMethodId: undefined,
  promotionProductCategoryId: undefined,
})
const params = ref({
  page: 1,
  pageSize: 10,
  total: 0,
})

const selectedCount = computed(() => allSelectedRowKeys.value.size)

const columns = computed(() => [
  {
    title: st('settingsWhitelist.colPublisher'),
    dataIndex: 'publisher',
    key: 'publisher',
    width: 220,
  },
  {
    title: st('settingsWhitelist.colBio'),
    dataIndex: 'bio',
    key: 'bio',
    width: 220,
  },
  {
    title: st('settingsWhitelist.colPromotionMethod'),
    dataIndex: 'promotionMethods',
    key: 'promotionMethods',
    width: 200,
  },
  {
    title: st('settingsWhitelist.colPromotionCategories'),
    dataIndex: 'promotionProductCategories',
    key: 'promotionProductCategories',
    width: 240,
  },
])

const formatNames = items => {
  if (!items?.length) return '--'
  if (typeof items[0] === 'string') return items.join(', ')
  return items.map(item => item.name).join(', ')
}

const onSelectChange = newSelectedRowKeys => {
  selectedRowKeys.value = newSelectedRowKeys
  const next = new Set(allSelectedRowKeys.value)
  const currentPageIds = data.value.map(item => item.publisherId)
  currentPageIds.forEach(id => next.delete(id))
  newSelectedRowKeys.forEach(key => next.add(key))
  allSelectedRowKeys.value = next
}

const setCurrentPageSelection = () => {
  selectedRowKeys.value = data.value
    .filter(item => allSelectedRowKeys.value.has(item.publisherId))
    .map(item => item.publisherId)
}

const onPageChange = ({ current, pageSize }) => {
  params.value.page = current
  params.value.pageSize = pageSize
  getList()
}

const onSearch = () => {
  params.value.page = 1
  getList()
}

const getList = async () => {
  loading.value = true
  try {
    const res = await whitelistApi.getEligiblePublishers({
      page: params.value.page,
      size: params.value.pageSize,
      keyword: keyword.value || undefined,
      promotionMethodIds: filter.value.promotionMethodId || undefined,
      promotionCategoryIds: filter.value.promotionProductCategoryId || undefined,
    })
    if (res.success) {
      const list = res.data?.data ?? res.data ?? []
      data.value = list.map(item => ({
        ...item,
        ...(item.publisherVO || {}),
      }))
      params.value.total = res.data?.total ?? list.length
      await nextTick()
      setCurrentPageSelection()
    }
  } finally {
    loading.value = false
  }
}

const initFilterOptions = async () => {
  const [categoryRes, methodRes] = await Promise.all([
    profileApi.getPromotionProductCategories(),
    profileApi.getPromotionMethods(),
  ])
  categories.value = (categoryRes.data || []).map(item => ({
    label: item.name,
    value: item.id,
  }))
  methods.value = (methodRes.data || []).map(item => ({
    label: item.name,
    value: item.id,
  }))
}

const clearAllSelection = () => {
  selectedRowKeys.value = []
  allSelectedRowKeys.value = new Set()
}

watch(
  () => [filter.value.promotionMethodId, filter.value.promotionProductCategoryId],
  () => {
    if (!visible.value) return
    params.value.page = 1
    getList()
  }
)

const handleSubmit = async () => {
  const publisherIds = Array.from(allSelectedRowKeys.value)
  if (!publisherIds.length) return
  submitting.value = true
  try {
    const res = await whitelistApi.addWhitelistPublisher({ publisherIds })
    if (res.success) {
      message.success(st('settingsWhitelist.addSuccess'))
      visible.value = false
      emit('refresh')
    } else {
      message.error(res.error)
    }
  } finally {
    submitting.value = false
  }
}

const handleCancel = () => {
  visible.value = false
}

const open = async () => {
  visible.value = true
  keyword.value = ''
  filter.value = {
    promotionMethodId: undefined,
    promotionProductCategoryId: undefined,
  }
  clearAllSelection()
  params.value.page = 1
  await initFilterOptions()
  getList()
}

defineExpose({ open })
</script>

<style lang="less" scoped>
.publisher-modal-content {
  .filter-bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .filter-left {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .filter-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .filter-label {
    font-size: 13px;
    font-weight: 600;
    color: #0f0629;
    letter-spacing: 0.3px;
    line-height: 18px;
  }

  .search-input {
    width: 280px;
    min-width: 200px;
  }
}

.footer-btn {
  border-top: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 20px;
  margin-top: 16px;
}

.selected-count {
  font-size: 14px;
  font-weight: 600;
  color: #0f0629;
  letter-spacing: 0.3px;
}
</style>
